import { apiClient } from "./apiClient";

/**
 * One row of POST /crm-integration-configs/bulk. The endpoint takes an array,
 * so a single-config save still ships a one-element list.
 *
 * Nullable fields are sent explicitly as null rather than omitted — the bulk
 * endpoint treats an absent key and a null the same way, and being explicit
 * keeps the payload readable against the API contract.
 */
export interface CrmIntegrationConfig {
  legalEntityId: string;
  branchId: string | null;
  /** Required — which level of the hierarchy the config binds to. */
  configLevel: string;
  /** Required — the CRM this config belongs to. */
  crmType: string;
  agencyRef: string | null;
  externalBranchId: string | null;
  username: string | null;
  password: string | null;
  customerCode: string | null;
  isActive: boolean;
  pollingEnabled: boolean;
  /** Null leaves the server-side default in place. */
  pollingIntervalMinutes: number | null;
  pageSize: number | null;
  rateLimitPerMinute: number | null;
  concurrentRequests: number | null;
}

/**
 * A saved config as the API returns it. Same shape as the request minus the
 * password — the endpoint never reads credentials back out.
 */
export interface CrmIntegrationConfigRecord extends Omit<CrmIntegrationConfig, "password"> {
  id: string;
  createdAt: string;
  updatedAt: string;
}

export const crmIntegrationConfigsService = {
  list: (legalEntityId: string) =>
    apiClient
      .get<CrmIntegrationConfigRecord[]>("/crm-integration-configs", {
        params: { legalEntityId },
      })
      .then((r) => r.data ?? []),

  bulkUpsert: (configs: CrmIntegrationConfig[]) =>
    apiClient.post("/crm-integration-configs/bulk", configs).then((r) => r.data),
};
